import { Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import Image from 'next/future/image';
import { ReebeloBatteryHealth } from '@lambda/reebelo';
import bhBg from '@/public/icons/battery-health-bg.svg';
import bhBolt from '@/public/icons/battery-bolt.svg';
import bhFull from '@/public/icons/battery-full.svg';
import bhMid from '@/public/icons/battery-mid.svg';
import {
  generateBatteryHealthDescription,
  generateBatteryHealthTitle,
} from '@/lib/collections/helpers';

interface BatteryUpgradeModalProps {
  isVisible: boolean;
  hideModal: () => void;
}

const Card = ({
  icon,
  title,
  description,
  subTitle,
  fancy = false,
}: {
  title: string;
  subTitle: string;
  description: string;
  icon: any;
  fancy?: boolean;
}) => (
  <div className="relative overflow-hidden rounded-lg border p-4">
    {fancy && (
      <div className="absolute top-0 left-0 h-1/2">
        <Image
          alt="Background"
          src={bhBg}
          height={200}
          width={400}
          className="h-full w-auto"
        />
      </div>
    )}
    <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#EDFBF4]">
      <div className="h-6 w-6">
        <Image
          alt="Background"
          src={icon}
          height={100}
          width={100}
          className="h-full w-full"
        />
      </div>
    </div>
    <h5 className="mt-2 text-base font-bold">{title}</h5>
    <h5 className="text-xs text-gray-400">{subTitle}</h5>
    <p className="mt-4">{description}</p>
  </div>
);

const BatteryHealthModal = ({
  hideModal,
  isVisible,
}: Readonly<BatteryUpgradeModalProps>) => (
  <Transition.Root show={isVisible} as={Fragment}>
    <Dialog as="div" className="relative z-50" onClose={hideModal}>
      <Transition.Child
        as={Fragment}
        enter="ease-in-out duration-500"
        enterFrom="opacity-0"
        enterTo="opacity-30"
        leave="ease-in-out duration-500"
        leaveFrom="opacity-30"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-gray-700" onClick={hideModal} />
      </Transition.Child>
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex w-full md:w-[350px]">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="pointer-events-auto relative w-full">
                <div className="relative flex h-full flex-col bg-white">
                  <div className="px-4 py-4 shadow sm:px-6">
                    <div className="flex items-start justify-between">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-bold leading-6 text-gray-700 xs:text-lg"
                      >
                        Battery Health
                      </Dialog.Title>
                      <button
                        onClick={hideModal}
                        type="button"
                        className="btn-close opacity-100 md:h-3 md:w-3"
                        aria-label="Close"
                      ></button>
                    </div>
                  </div>
                  {/* Content  */}
                  <div className="overflow-y-auto text-center text-sm">
                    <div className="space-y-3 p-5">
                      <Card
                        title={generateBatteryHealthTitle(
                          ReebeloBatteryHealth.BH_80,
                        )}
                        subTitle={generateBatteryHealthDescription(
                          ReebeloBatteryHealth.BH_80,
                        )}
                        description="A reliable battery with at least 80% of its original capacity, ensuring solid everyday performance."
                        icon={bhMid}
                      />
                      <Card
                        title={generateBatteryHealthTitle(
                          ReebeloBatteryHealth.BH_90,
                        )}
                        subTitle={generateBatteryHealthDescription(
                          ReebeloBatteryHealth.BH_90,
                        )}
                        description="A high-capacity battery with at least 90% of its original health, offering extended usage and longevity."
                        icon={bhFull}
                      />

                      <Card
                        title={generateBatteryHealthTitle(
                          ReebeloBatteryHealth.BH_100,
                        )}
                        subTitle={generateBatteryHealthDescription(
                          ReebeloBatteryHealth.BH_100,
                        )}
                        description="A brand-new battery at full capacity, delivering maximum power and performance—just like a new device."
                        icon={bhBolt}
                        fancy
                      />
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </div>
    </Dialog>
  </Transition.Root>
);

export default BatteryHealthModal;
